import {includes, isNil, toLower, uniq} from 'ramda';
const URL_REGEXP =
  /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/;

export const Validator = {
  required,
  unique,
  allowBlank,
  isNullUndefinedEmptyString,
  validNumber,
  validInteger,
  validNatural,
  validPercentage,
  validUrl,
  positive,
  strictlyPositive,
  validateZipcode,
  validateEmail,
  validateUniquePhone,
  stringMatch,
};
export function required(value: Maybe<string | number | boolean>): Maybe<string> {
  return isNullUndefinedEmptyString(value) ? 'is required' : null;
}
export function stringMatch(val1: unknown, val2: unknown): Maybe<string> {
  return String(val1) === String(val2) ? null : 'does not match';
}
export function unique<T>(value: T, values: T[]): Maybe<string> {
  return values.includes(value) ? 'already taken' : null;
}
export function allowBlank(fn: (value: Maybe<string | number>) => boolean) {
  return value => (isNullUndefinedEmptyString(value) ? true : fn(value));
}
export function validNumber(value: Maybe<string | number>): boolean {
  return !isNil(value) && !isNaN(+value);
}
export function validInteger(value: Maybe<string | number>): boolean {
  return validNumber(value) && parseInt(value) == parseFloat(value);
}
export function validUrl(value: unknown): boolean {
  if (!value || !String(value)) return false;
  return !!String(value).match(URL_REGEXP);
}
export function validNatural(value: Maybe<string | number>): boolean {
  return validInteger(value) && parseInt(value) >= 0;
}
export function validPercentage(value: Maybe<string | number>): boolean {
  return positive(value) && validBetweenZeroAndHundred(value);

  function validBetweenZeroAndHundred(integer) {
    const number = parseInt(integer);
    return number >= 0 && number <= 100;
  }
}
export function positive(value: Maybe<string | number>): boolean {
  return validNumber(value) && parseFloat(value) >= 0;
}
export function strictlyPositive(value: Maybe<string | number>): boolean {
  return validNumber(value) && parseFloat(value) > 0;
}
function isNullUndefinedEmptyString(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}
export function validateZipcode(zipcode: Maybe<string>): Maybe<string> {
  if (!zipcode) return 'is required';
  if (!/(^\d{5}$)|(^\d{5}-\d{4}$)/.test(zipcode)) return 'is not valid';
  return null;
}
// This was copied of emailregex.com, it's not a perfect solution but it works
// for most cases

/* eslint-disable no-useless-escape */
export const EMAIL_REGEX =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/* eslint-enable no-useless-escape */
export function validateEmail(
  email: Maybe<string>,
  emails?: Maybe<string>[] | null | undefined,
  notRequired?: boolean,
): Maybe<string> {
  if (!email) return notRequired ? null : 'Email is required';
  if (!EMAIL_REGEX.test(email)) return 'Email is invalid';
  if (
    includes(
      email.toLowerCase(),
      uniq(emails || [])
        .filter(Boolean)
        .map(toLower),
    )
  ) {
    return 'Email already taken';
  }
  return null;
}
export const STANDARD_PHONE_REGEX = /^\d{3}-\d{3}-\d{4}?$/;
export const NOT_STANDARD_PHONE_REGEX = /^\d{3}-\d{3}-\d{4}(-\d+)?$/;
export function validateUniquePhone(phone: string, phones: string[] = []): Maybe<string> {
  if (includes(phone, uniq(phones))) return 'already taken';
  return null;
}
export function notNull<T>(value: Maybe<T>): value is T {
  return value !== null && value !== undefined;
}
export function notFalse<T>(value: T | null | undefined | false): value is T {
  return value !== null && value !== undefined && value !== false;
}
export const validatePhone = (phone: Maybe<string>) => {
  if (!phone) return 'Phone is required';
  if (phone.replace(/\D/g, '').length !== 10) return 'Phone must have 10 digits';
  return null;
};
export function validateEmailOrPhone(emailOrPhone: Maybe<string>): Maybe<string> {
  if (!emailOrPhone) return 'E-mail or phone is invalid';
  if (isEmail(emailOrPhone)) return validateEmail(emailOrPhone);
  return validatePhone(emailOrPhone);
}
export function isEmail(emailOrPhone: Maybe<string>) {
  return emailOrPhone?.replace(/[()\s-]/g, '').match(/\D/);
}
